import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { NavLink, useLocation } from 'react-router-dom';
import SidebarLinkGroup from './SidebarLinkGroup';
import ElTestLogo from '../../images/logo/logo.png'; // Import the new logo
import { getUserInfo } from '../../utils/remote';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const { role } = useAuth();
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
  );

  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [institutionType, setInstitutionType] = useState<string | null>(null);

  const isUniversity = (institutionType || '').toString().toLowerCase().includes('university') || (institutionType || '').toString().toLowerCase().includes('college') || role === 'college_university';

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add('sidebar-expanded');
    } else {
      document.querySelector('body')?.classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  // Fetch institution data
  useEffect(() => {
    const fetchInstitutionData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const data = await getUserInfo(token || undefined);
        setInstitutionName(data.institution_name || null);
        setInstitutionType(data.institution_type || null);
      } catch (error) {
        console.error("Error fetching institution data:", error);
      }
    };
    fetchInstitutionData();
  }, [role]); // Re-fetch if role changes, assuming institution might change with role

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex flex-col gap-2 px-6 py-5.5 lg:py-6.5">
        <div className="flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2">
            <img src={ElTestLogo} alt="El-Test Logo" className="w-8 h-8" />
            <h2 className="text-2xl font-bold text-eltest-blue">El-Test</h2>
          </NavLink>

          <button
            ref={trigger}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
            className="block lg:hidden"
            title="Toggle Sidebar"
          >
            <svg
              className="fill-current"
              width="20"
              height="18"
              viewBox="0 0 20 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
                fill=""
              />
            </svg>
          </button>
        </div>
        {(institutionName && institutionType) && (
          <div className="text-sm font-medium text-eltest-orange">
            {institutionName} ({institutionType})
          </div>
        )}
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
          {/* <!-- Menu Group --> */}
          <div>
            <h3 className="mb-4 ml-4 text-sm font-semibold text-bodydark2">
              MENU
            </h3>

            <ul className="mb-6 flex flex-col gap-1.5">
              {(role === 'school' || role === 'superuser') && (
                <SidebarLinkGroup
                  activeCondition={pathname === '/students' || pathname.startsWith('/students/')}
                >
                  {(handleClick, open) => (
                    <>
                      <NavLink
                        to="#"
                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${(pathname === '/students' || pathname.startsWith('/students/')) && 'bg-graydark dark:bg-meta-4'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                        }}
                      >
                        <svg
                          className="fill-current"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.0002 7.79065C11.0814 7.79065 12.7689 6.1594 12.7689 4.1344C12.7689 2.1094 11.0814 0.478149 9.0002 0.478149C6.91895 0.478149 5.23145 2.1094 5.23145 4.1344C5.23145 6.1594 6.91895 7.79065 9.0002 7.79065ZM9.0002 1.7719C10.3783 1.7719 11.5033 2.84065 11.5033 4.16252C11.5033 5.4844 10.3783 6.55315 9.0002 6.55315C7.62207 6.55315 6.49707 5.4844 6.49707 4.16252C6.49707 2.84065 7.62207 1.7719 9.0002 1.7719Z"
                            fill=""
                          />
                          <path
                            d="M10.8283 9.05627H7.17207C4.16269 9.05627 1.71582 11.5313 1.71582 14.5406V16.875C1.71582 17.2125 1.99707 17.5219 2.36270 17.5219C2.72832 17.5219 3.00957 17.2407 3.00957 16.875V14.5406C3.00957 12.2344 4.89394 10.3219 7.22832 10.3219H10.8564C13.1627 10.3219 15.0752 12.2063 15.0752 14.5406V16.875C15.0752 17.2125 15.3564 17.5219 15.7221 17.5219C16.0877 17.5219 16.3689 17.2407 16.3689 16.875V14.5406C16.2846 11.5313 13.8377 9.05627 10.8283 9.05627Z"
                            fill=""
                          />
                        </svg>
                        Students
                        <svg
                          className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${open && 'rotate-180'}`}
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                            fill=""
                          />
                        </svg>
                      </NavLink>
                      <div className={`translate transform overflow-hidden ${!open && 'hidden'}`}>
                        <ul className="mt-4 mb-5.5 flex flex-col gap-2.5 pl-6">
                          <li>
                            <NavLink
                              to="/students/requests"
                              end
                              className={({ isActive }) =>
                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                (isActive && '!text-white')
                              }
                            >
                              Student Requests
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/students"
                              end
                              className={({ isActive }) =>
                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                (isActive && '!text-white')
                              }
                            >
                              Student List
                            </NavLink>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </SidebarLinkGroup>
              )}
              <SidebarLinkGroup
                activeCondition={
                  pathname === '/exams' || pathname.includes('exams')
                }
              >
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="#"
                      className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${(pathname === '/exams' || pathname.includes('exams')) && 'bg-graydark dark:bg-meta-4'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dee4ee"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M13 2H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V9l-7-7z" />
                        <path d="M13 3v6h6" />
                      </svg>
                      Exams
                      <svg
                        className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${open && 'rotate-180'}`}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                          fill=""
                        />
                      </svg>
                    </NavLink>
                    <div className={`translate transform overflow-hidden ${!open && 'hidden'}`}>
                      <ul className="mt-4 mb-5.5 flex flex-col gap-2.5 pl-6">
                        <li>
                          <NavLink
                            to="/exams/exam-list"
                            className={({ isActive }) =>
                              'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                              (isActive && '!text-white')
                            }
                          >
                            List Exam
                          </NavLink>
                        </li>
                        {(role === 'school' || role === 'college_university' || role === 'superuser') && (
                          <li>
                            <NavLink
                              to="/exams/add-exam"
                              className={({ isActive }) =>
                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                (isActive && '!text-white')
                              }
                            >
                              Add Exam
                            </NavLink>
                          </li>
                        )}
                        {(role === 'college_university' || role === 'superuser') && (
                          <li>
                            <NavLink
                              to="/exams/mixed-exam"
                              className={({ isActive }) =>
                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                (isActive && '!text-white')
                              }
                            >
                              Mixed Exam
                            </NavLink>
                          </li>
                        )}
                        {(role === 'school' || role === 'college_university' || role === 'superuser') && (
                          <li>
                            <NavLink
                              to="/exams/generate"
                              className={({ isActive }) =>
                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                (isActive && '!text-white')
                              }
                            >
                              Generate Exam
                            </NavLink>
                          </li>
                        )}
                      </ul>
                    </div>
                  </>
                )}
              </SidebarLinkGroup>

              {/* School Setup */}
              {(role === 'school' || role === 'college_university' || role === 'superuser') && (
                <SidebarLinkGroup
                  activeCondition={pathname.includes('school-setup')}
                >
                  {(handleClick, open) => (
                    <>
                      <NavLink
                        to="#"
                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('school-setup') && 'bg-graydark dark:bg-meta-4'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dee4ee" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16v4H4z" />
                          <path d="M4 12h16v8H4z" />
                        </svg>
                        {isUniversity ? 'University Setup' : 'School Setup'}
                        <svg
                          className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${open && 'rotate-180'}`}
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                            fill=""
                          />
                        </svg>
                      </NavLink>
                      <div className={`translate transform overflow-hidden ${!open && 'hidden'}`}>
                        <ul className="mt-4 mb-5.5 flex flex-col gap-2.5 pl-6">
                          {(role === 'school' || role === 'college_university' || role === 'superuser') && (
                            <>
                              <li>
                                <NavLink
                                  to="/school-setup/class"
                                  className={({ isActive }) =>
                                    'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                    (isActive && '!text-white')
                                  }
                                >
                                  {isUniversity ? 'Department Setup' : 'Class Setup'}
                                </NavLink>
                              </li>
                              <li>
                                <NavLink
                                  to="/school-setup/chapters"
                                  className={({ isActive }) =>
                                    'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                    (isActive && '!text-white')
                                  }
                                >
                                  Chapter Setup
                                </NavLink>
                              </li>
                              <li>
                                <NavLink
                                  to="/school-setup/teachers"
                                  className={({ isActive }) =>
                                    'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                    (isActive && '!text-white')
                                  }
                                >
                                  Teachers
                                </NavLink>
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </>
                  )}
                </SidebarLinkGroup>
              )}

              {/* Results */}
              {(role === 'school' || role === 'college_university' || role === 'superuser') && (
                <li>
                  <NavLink
                    to="/results"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('results') && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg
                      className="fill-current"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 2.25C4.725 2.25 1.5 5.475 1.5 9.75C1.5 14.025 4.725 17.25 9 17.25C13.275 17.25 16.5 14.025 16.5 9.75C16.5 5.475 13.275 2.25 9 2.25ZM9 15.75C8.175 15.75 7.5 15.075 7.5 14.25C7.5 13.425 8.175 12.75 9 12.75C9.825 12.75 10.5 13.425 10.5 14.25C10.5 15.075 9.825 15.75 9 15.75ZM9 11.25C8.175 11.25 7.5 10.575 7.5 9.75C7.5 8.925 8.175 8.25 9 8.25C9.825 8.25 10.5 8.925 10.5 9.75C10.5 10.575 9.825 11.25 9 11.25ZM9 6.75C8.175 6.75 7.5 6.075 7.5 5.25C7.5 4.425 8.175 3.75 9 3.75C9.825 3.75 10.5 4.425 10.5 5.25C10.5 6.075 9.825 6.75 9 6.75Z"
                        fill=""
                      />
                    </svg>
                    Results
                  </NavLink>
                </li>
              )}

              {/* Settings */}
              <li>
                <NavLink
                  to="/settings"
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('settings') && 'bg-graydark dark:bg-meta-4'}`}
                >
                  <svg
                    className="fill-current"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 2.25C4.725 2.25 1.5 5.475 1.5 9.75C1.5 13.425 4.725 16.5 9 16.5C13.275 16.5 16.5 13.425 16.5 9.75C16.5 5.475 13.275 2.25 9 2.25ZM9 14.25C8.175 14.25 7.5 13.575 7.5 12.75C7.5 11.925 8.175 11.25 9 11.25C9.825 11.25 10.5 11.925 10.5 12.75C10.5 13.575 9.825 14.25 9 14.25ZM9 9.75C8.175 9.75 7.5 9.075 7.5 8.25C7.5 7.425 8.175 6.75 9 6.75C9.825 6.75 10.5 7.425 10.5 8.25C10.5 9.075 9.825 9.75 9 9.75ZM9 5.25C8.175 5.25 7.5 4.575 7.5 3.75C7.5 2.925 8.175 2.25 9 2.25C9.825 2.25 10.5 2.925 10.5 3.75C10.5 4.575 9.825 5.25 9 5.25Z"
                      fill=""
                    />
                  </svg>
                  Settings
                </NavLink>
              </li>
            </ul>
          </div>

          {role === 'superuser' && (
            <div className="mt-6">
              <h3 className="mb-4 ml-4 text-sm font-semibold text-bodydark2">ADMIN</h3>
              <ul className="mb-6 flex flex-col gap-1.5">
                <li>
                  <NavLink
                    to="/admin"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname === '/admin' && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 3h12v12H3z" fill="none" />
                      <path d="M9 1l3 3H7L9 1z" fill="#dee4ee" />
                    </svg>
                    Dashboard
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/admin/users"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('/admin/users') && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 9a4 4 0 100-8 4 4 0 000 8zM2 16a7 7 0 0114 0v1H2v-1z" fill="" />
                    </svg>
                    Manage Users
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/admin/schools"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('/admin/schools') && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 7l6-4 6 4v8H3V7z" fill="" />
                    </svg>
                    Manage Institute
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/admin/exams"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('/admin/exams') && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 3h10v12H4z" fill="none" />
                      <path d="M6 5h6M6 8h6M6 11h6" stroke="#dee4ee" strokeWidth="1.5" />
                    </svg>
                    Manage Exams
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/admin/students"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('/admin/students') && 'bg-graydark dark:bg-meta-4'}`}
                  >
                    <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 2l7 3-7 3-7-3 7-3zm0 8l5-2.142V13L9 15l-5-2V7.858L9 10z" fill="" />
                    </svg>
                    Manage Students
                  </NavLink>
                </li>
                <li>
                  <SidebarLinkGroup
                    activeCondition={pathname.includes('/admin/rag') || pathname.includes('/admin/rag-setup')}
                  >
                    {(handleClick, open) => (
                      <>
                        <NavLink
                          to="#"
                          className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${(pathname.includes('/admin/rag') || pathname.includes('/admin/rag-setup')) && 'bg-graydark dark:bg-meta-4'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                          }}
                        >
                          <svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 3h12v12H3z" fill="none" />
                            <path d="M5 5h8v2H5zM5 8h8v2H5zM5 11h5v2H5z" fill="" />
                          </svg>
                          RAG
                          <svg
                            className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${open && 'rotate-180'}`}
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                              fill=""
                            />
                          </svg>
                        </NavLink>
                        <div className={`translate transform overflow-hidden ${!open && 'hidden'}`}>
                          <ul className="mt-4 mb-5.5 flex flex-col gap-2.5 pl-6">
                            <li>
                              <NavLink
                                to="/admin/rag"
                                className={({ isActive }) =>
                                  'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                  (isActive && '!text-white')
                                }
                              >
                                List RAG
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/admin/rag-setup"
                                className={({ isActive }) =>
                                  'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                  (isActive && '!text-white')
                                }
                              >
                                Add RAG
                              </NavLink>
                            </li>
                          </ul>
                        </div>
                      </>
                    )}
                  </SidebarLinkGroup>
                </li>
              </ul>
            </div>
          )}

          {/* <!-- Others Group removed: not used in ElTest --> */}
        </nav>
        {/* <!-- Sidebar Menu --> */}
      </div>
    </aside>
  );
};

export default Sidebar;
